import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  InputLabel,
  FormControl,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { Client, CustomField } from "0.Mine/3.Interfaces/Client";

interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, client: Partial<Client>) => Promise<void>;
  client: Client | null;
  customFields: CustomField[];
}

export function EditClientModal({
  isOpen,
  onClose,
  onSave,
  client,
  customFields,
}: EditClientModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<Client>>({});

  useEffect(() => {
    if (client) {
      setFormData({
        ...client,
      });
    }
  }, [client]);

  // Generic change handler for text inputs
  const handleTextInputChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    if (name) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Specific handler for MUI Select components
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    if (name) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const tags = e.target.value
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    setFormData((prev) => ({
      ...prev,
      tags,
    }));
  };

  const handleCustomFieldChange = (id: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      customFields: {
        ...prev?.customFields,
        [id]: value,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;
    setIsLoading(true);
    try {
      await onSave(client.id, formData);
      onClose();
    } catch (error) {
      console.error("Failed to update client:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!client) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "primary.main",
          color: "primary.contrastText",
          py: 2,
        }}
      >
        Edit Client
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 4 }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                name="name"
                value={formData.name || ""}
                onChange={handleTextInputChange}
                required
                fullWidth
                variant="outlined"
                margin="normal"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email || ""}
                onChange={handleTextInputChange}
                required
                fullWidth
                variant="outlined"
                margin="normal"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone"
                name="phone"
                value={formData.phone || ""}
                onChange={handleTextInputChange}
                fullWidth
                variant="outlined"
                margin="normal"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Company"
                name="company"
                value={formData.company || ""}
                onChange={handleTextInputChange}
                fullWidth
                variant="outlined"
                margin="normal"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" size="small">
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  value={formData.status || ""}
                  onChange={handleSelectChange}
                  label="Status"
                >
                  <MenuItem value="active">Attivo</MenuItem>
                  <MenuItem value="inactive">Inattivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Last Contact"
                name="lastContact"
                type="date"
                value={formData.lastContact || ""}
                onChange={handleTextInputChange}
                fullWidth
                variant="outlined"
                margin="normal"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Value ($)"
                name="value"
                type="number"
                value={formData.value?.toString() || "0"}
                onChange={handleTextInputChange}
                fullWidth
                variant="outlined"
                margin="normal"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Tags (comma-separated)"
                name="tags"
                value={formData.tags?.join(", ") || ""}
                onChange={handleTagChange}
                fullWidth
                variant="outlined"
                margin="normal"
                size="small"
              />
            </Grid>
          </Grid>

          {customFields.length > 0 && (
            <Box mt={4} pt={2}>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="h6" gutterBottom>
                Custom Fields
              </Typography>
              <Grid container spacing={3}>
                {customFields.map((field) => (
                  <Grid item xs={12} md={6} key={field.id}>
                    {field.type === "select" ? (
                      <FormControl fullWidth margin="normal" size="small">
                        <InputLabel id={`custom-field-${field.id}-label`}>{field.name}</InputLabel>
                        <Select
                          labelId={`custom-field-${field.id}-label`}
                          value={formData.customFields?.[field.id] || ""}
                          onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                          label={field.name}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {field.options?.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        label={field.name}
                        type={
                          field.type === "date"
                            ? "date"
                            : field.type === "number"
                            ? "number"
                            : "text"
                        }
                        value={formData.customFields?.[field.id] || ""}
                        onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        size="small"
                        InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
